/**
 * Started / Mastered snippet counts — same visual language as RoadmapList topic rows.
 * Optional leading snippet total (<> N) for cookbook cards; progress metrics sit on the right.
 * Display-only by default; pass onStartedClick / onMasteredClick to make labels actionable.
 */

import React from 'react';
import { ReactComponent as CookbookSnippetCountIcon } from '../../Icons/deck-icon.svg';
import {
  formatSnippetMasteryCountAria,
  formatSnippetMasteryCountTitle,
  SNIPPET_MASTERY_LABEL
} from '../../constants/snippetMasteryCopy';
import './CookbookSnippetProgressStats.css';

function StudiedProgressRing({ started, total, className }) {
  const safeTotal = Math.max(1, Number(total) || 1);
  const safeStarted = Math.max(0, Math.min(safeTotal, Number(started) || 0));
  const progress = safeStarted / safeTotal;
  const size = 12;
  const stroke = 1.75;
  const radius = (size - stroke) / 2 - 0.25;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      className={className}
    >
      <circle
        className="cookbook-snippet-progress-stats__studied-ring-track"
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
      />
      <circle
        className="cookbook-snippet-progress-stats__studied-ring-fill"
        cx={cx}
        cy={cy}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
    </svg>
  );
}

function MasteredProgressRing({ mastered, total, className }) {
  const safeTotal = Math.max(1, Number(total) || 1);
  const safeMastered = Math.max(0, Math.min(safeTotal, Number(mastered) || 0));
  const isComplete = safeMastered >= safeTotal;
  const hasProgress = safeMastered > 0;
  const progress = safeMastered / safeTotal;
  const size = 12;
  const stroke = 1.75;
  const radius = (size - stroke) / 2 - 0.25;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progress);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
      className={[
        className,
        isComplete ? 'cookbook-snippet-progress-stats__mastered-ring--complete' : '',
        hasProgress && !isComplete ? 'cookbook-snippet-progress-stats__mastered-ring--active' : ''
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isComplete ? (
        <circle
          className="cookbook-snippet-progress-stats__mastered-ring-badge"
          cx={cx}
          cy={cy}
          r={radius + 0.2}
        />
      ) : (
        <>
          <circle
            className="cookbook-snippet-progress-stats__mastered-ring-track"
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
          />
          {hasProgress ? (
            <circle
              className="cookbook-snippet-progress-stats__mastered-ring-fill"
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          ) : null}
        </>
      )}
      <path
        className="cookbook-snippet-progress-stats__mastered-ring-check"
        d="M3.35 6.05 L5.05 7.75 L8.65 4.15"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.35"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * @param {{
 *   started?: number,
 *   mastered?: number,
 *   total?: number,
 *   loading?: boolean,
 *   className?: string,
 *   cookbookTitle?: string,
 *   showSnippetCount?: boolean,
 *   onStartedClick?: (e: React.MouseEvent) => void,
 *   onMasteredClick?: (e: React.MouseEvent) => void,
 *   startedDisabled?: boolean,
 *   masteredDisabled?: boolean,
 * }} props
 */
export default function CookbookSnippetProgressStats({
  started = 0,
  mastered = 0,
  total = 0,
  loading = false,
  className = '',
  cookbookTitle = '',
  showSnippetCount = true,
  onStartedClick,
  onMasteredClick,
  startedDisabled = false,
  masteredDisabled = false
}) {
  if (!loading && total <= 0) return null;

  const safeStarted = Number(started) || 0;
  const safeMastered = Number(mastered) || 0;
  const safeTotal = Number(total) || 0;
  const allStarted = safeTotal > 0 && safeStarted === safeTotal;
  const allMastered = safeTotal > 0 && safeMastered === safeTotal;

  const studiedVariant =
    safeStarted === 0
      ? 'cookbook-snippet-progress-stats__label--none'
      : allStarted
        ? 'cookbook-snippet-progress-stats__label--complete'
        : 'cookbook-snippet-progress-stats__label--active';
  const masteredVariant = allMastered
    ? 'cookbook-snippet-progress-stats__label--complete'
    : safeMastered > 0
      ? 'cookbook-snippet-progress-stats__label--active'
      : 'cookbook-snippet-progress-stats__label--none';

  const titleSuffix = cookbookTitle ? ` in ${cookbookTitle}` : '';
  const startedCountText = loading ? '…' : `${safeStarted}/${safeTotal || '…'}`;
  const masteredCountText = loading ? '…' : `${safeMastered}/${safeTotal || '…'}`;
  const snippetCountText = loading ? '…' : String(safeTotal);

  const StartedTag = onStartedClick ? 'button' : 'span';
  const MasteredTag = onMasteredClick ? 'button' : 'span';

  return (
    <span
      className={[
        'cookbook-snippet-progress-stats',
        showSnippetCount ? 'cookbook-snippet-progress-stats--with-count' : '',
        className
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {showSnippetCount ? (
        <span
          className="cookbook-snippet-progress-stats__snippet-count"
          title={loading ? 'Loading snippet count' : `${safeTotal} snippets`}
          aria-label={loading ? 'Loading snippet count' : `${safeTotal} snippets`}
        >
          <span className="cookbook-snippet-progress-stats__snippet-count-icon-wrap" aria-hidden>
            <CookbookSnippetCountIcon className="cookbook-snippet-progress-stats__snippet-count-icon" />
          </span>
          <span className="cookbook-snippet-progress-stats__snippet-count-value">
            {snippetCountText}
          </span>
        </span>
      ) : null}

      <span className="cookbook-snippet-progress-stats__metrics">
        <StartedTag
          type={onStartedClick ? 'button' : undefined}
          className={[
            'cookbook-snippet-progress-stats__label',
            'cookbook-snippet-progress-stats__label--studied',
            studiedVariant
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={onStartedClick}
          disabled={onStartedClick ? startedDisabled : undefined}
          title={
            loading
              ? 'Loading progress'
              : `${safeStarted}/${safeTotal} snippets started${titleSuffix}`
          }
          aria-label={`Started ${safeStarted} of ${safeTotal} snippets${titleSuffix}`}
        >
          <StudiedProgressRing
            started={safeStarted}
            total={safeTotal || 1}
            className="cookbook-snippet-progress-stats__studied-ring"
          />
          <span className="cookbook-snippet-progress-stats__label-name">Started</span>
          <span className="cookbook-snippet-progress-stats__label-count">{startedCountText}</span>
        </StartedTag>

        <MasteredTag
          type={onMasteredClick ? 'button' : undefined}
          className={[
            'cookbook-snippet-progress-stats__label',
            'cookbook-snippet-progress-stats__label--mastered',
            masteredVariant
          ]
            .filter(Boolean)
            .join(' ')}
          onClick={onMasteredClick}
          disabled={onMasteredClick ? masteredDisabled : undefined}
          title={
            loading
              ? 'Loading progress'
              : formatSnippetMasteryCountTitle(safeMastered, safeTotal, titleSuffix)
          }
          aria-label={formatSnippetMasteryCountAria(safeMastered, safeTotal, titleSuffix)}
        >
          <MasteredProgressRing
            mastered={safeMastered}
            total={safeTotal || 1}
            className="cookbook-snippet-progress-stats__mastered-ring"
          />
          <span className="cookbook-snippet-progress-stats__label-name">{SNIPPET_MASTERY_LABEL}</span>
          <span className="cookbook-snippet-progress-stats__label-count">{masteredCountText}</span>
        </MasteredTag>
      </span>
    </span>
  );
}
